const EXPIRED_STATUSES = new Set([
    'expired',
    'cancelled',
    'canceled',
    'past_due',
    'unpaid',
    'trial_expired',
    'no_subscription',
])

const ACTIVE_STATUSES = new Set(['active', 'trialing'])
const INACTIVE_STATUSES = new Set(['inactive', 'deactivated'])
const ARCHIVED_STATUSES = new Set(['archived'])

function normalizeStatus(value) {
    return String(value || '').toLowerCase().trim()
}

function isNoActivePlan(planName) {
    const plan = normalizeStatus(planName)
    return !plan
        || plan === 'n/a'
        || plan === 'none'
        || plan.includes('no active subscription')
}

function getRenewalDate(company) {
    return company.renewalDate
        ?? company.RenewalDate
        ?? company.currentPeriodEnd
        ?? company.CurrentPeriodEnd
        ?? null
}

/** Subscription period ended if renewal/end date is before today. */
export function isRenewalDatePast(dateValue) {
    if (!dateValue) return false

    const date = new Date(dateValue)
    if (Number.isNaN(date.getTime())) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)

    return date < today
}

export function resolveCompanySubscriptionStatus(company = {}) {
    const subscriptionStatus = normalizeStatus(
        company.subscriptionStatus ?? company.SubscriptionStatus
    )
    const accountStatus = normalizeStatus(company.status ?? company.Status)
    const planName = company.planName || company.plan || company.planType || ''
    const renewalDate = getRenewalDate(company)

    if (company.isArchived === true || company.IsArchived === true || ARCHIVED_STATUSES.has(accountStatus)) {
        return 'Archived'
    }

    if (company.isActive === false || company.IsActive === false || INACTIVE_STATUSES.has(accountStatus)) {
        return 'Inactive'
    }

    if (EXPIRED_STATUSES.has(subscriptionStatus) || EXPIRED_STATUSES.has(accountStatus)) {
        return 'Expired'
    }

    if (isNoActivePlan(planName)) {
        return 'Expired'
    }

    const isTrial = company.isTrialAccount === true
        || normalizeStatus(planName) === 'trial'

    if (isTrial) {
        const trialEnd = company.trialEndDate || company.trialEndsAt || renewalDate
        if (isRenewalDatePast(trialEnd)) {
            return 'Expired'
        }
        if (accountStatus === 'expired') {
            return 'Expired'
        }
        return 'Active'
    }

    if (isRenewalDatePast(renewalDate)) {
        return 'Expired'
    }

    if (ACTIVE_STATUSES.has(subscriptionStatus) || ACTIVE_STATUSES.has(accountStatus)) {
        return 'Active'
    }

    if (accountStatus) {
        return accountStatus.charAt(0).toUpperCase() + accountStatus.slice(1)
    }

    if (subscriptionStatus) {
        return subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)
    }

    return 'Active'
}

export function getCompanyStatusDisplay(status) {
    const normalized = normalizeStatus(status)

    if (INACTIVE_STATUSES.has(normalized)) {
        return {
            label: 'Inactive',
            dotColor: '#d97706',
            textColor: '#b45309',
            badgeBg: '#d97706',
        }
    }

    if (ARCHIVED_STATUSES.has(normalized)) {
        return {
            label: 'Archived',
            dotColor: '#6b7280',
            textColor: '#4b5563',
            badgeBg: '#6b7280',
        }
    }

    const isExpired = EXPIRED_STATUSES.has(normalized) || normalized === 'expired'

    if (isExpired) {
        return {
            label: 'Expired',
            dotColor: '#dc2626',
            textColor: '#dc2626',
            badgeBg: '#dc2626',
        }
    }

    return {
        label: 'Active',
        dotColor: '#7ED321',
        textColor: '#059669',
        badgeBg: '#7ED321',
    }
}
